import { useEffect, useState } from 'react';

/**
 * Camera-change tick: EngineViewport's OrbitControls (plus the zoom/pan
 * command handlers) dispatch 'yap:camera-changed' after every framing
 * update; consumers that derive geometry from the visible region (infinite
 * construction lines) subscribe with this hook and re-render on tick change.
 */
let globalTick = 0;

export function notifyCameraChanged(): void {
    globalTick += 1;
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('yap:camera-changed', { detail: { tick: globalTick } }));
    }
}

/**
 * Apply camera framing from non-canvas code (MCP handlers): dispatch the
 * same events the viewport ZoomChordHandler listens for, then notify the
 * camera-tick subscribers so view-derived geometry (infinite construction
 * lines) re-clips to the new framing.
 */
export function requestCameraFraming(
    kind: 'region' | 'scale' | 'pan',
    detail: Record<string, unknown>
): void {
    const name = kind === 'region' ? 'yap:zoom-region' : kind === 'scale' ? 'yap:zoom-scale' : 'yap:pan-by';
    window.dispatchEvent(new CustomEvent(name, { detail }));
    notifyCameraChanged();
}

export function useCameraTick(): number {
    const [tick, setTick] = useState(globalTick);
    useEffect(() => {
        const on = () => setTick(globalTick + 1);
        window.addEventListener('yap:camera-changed', on);
        window.addEventListener('resize', on);
        return () => {
            window.removeEventListener('yap:camera-changed', on);
            window.removeEventListener('resize', on);
        };
    }, []);
    return tick;
}
